import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceFromLine, scaledCoordinate } from '../drawing.helper'
import { waveDegreeOptions } from './elliottWaveConstants'

export type ElliottImpulseWaveState = {
    a?: Point,
    b?: Point,
    c?: Point,
    d?: Point,
    e?: Point,
    f?: Point
}

export type ElliottImpulseWaveSettings = {
    lineColor: ColorValue,
    degree: string,
    lineWidth: number,
    point1: Point,
    point2: Point,
    point3: Point,
    point4: Point,
    point5: Point,
    point6: Point,
}

export type ElliotteWaveOptions = {
    triangleWave: boolean
}


const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

const romanNumbers: string[] = ['O', 'I', 'II', 'III', 'IV', 'V']
const lowerRomanNumbers: string[] = ['0', 'i', 'ii', 'iii', 'iv', 'v']

class ElliottImpulseWaveTool extends DrawingBase<any, ElliottImpulseWaveSettings, ElliottImpulseWaveState> {
    scaledCoordinates: Record<string, PointCoordinates> = {}
    lengths: Record<string, number> = {}
    slopes: Record<string, number> = {}
    intercepts: Record<string, number> = {}

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { a, b, c, d, e, f } = this.scaledCoordinates

        let prevPoint: PointCoordinates

        ctx.beginPath()
        this.setLineAttributes(ctx)
        const points = [a, b, c, d, e, f]
        points.forEach((p, i) => {
            if (!p) return

            if (!prevPoint) {
                ctx.moveTo(p.x, p.y)
            } else {
                ctx.lineTo(p.x, p.y)
            }
            prevPoint = p
            ctx.globalAlpha = this._settings.lineColor.opacity
            ctx.stroke()
        })

        this.drawPointsLabels(ctx)

    };

    drawPointsLabels(ctx: CanvasRenderingContext2D) {
        const { a, b, c, d, e, f } = this._state
        const points = [a, b, c, d, e, f].filter(v => v)

        const { degree } = this._settings
        const { triangleWave } = this.options
        points.forEach((p, i) => {
            const refPoint = i == 0 ? i + 1 : i - 1

            const position = points[refPoint] && points[refPoint]!.y > p!.y ? -1 : 1
            const renderCoords = this.scaledCoordinates[p!.id]
            const text = this.getText(triangleWave && i > 0 ? points[i - 1]!.id : i.toString())
            ctx.font = (this.getFontSize() * window.devicePixelRatio) + 'px sans-serif'
            ctx.textBaseline = 'top'
            ctx.globalAlpha = 1
            ctx.lineWidth = 2 * window.devicePixelRatio
            ctx.strokeStyle = this._settings.lineColor.color
            const padding = 6 * window.devicePixelRatio
            const dim = ctx.measureText(text)
            const textWidth = dim.width
            const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
            const startX = renderCoords.x - textWidth / 2
            const startY = renderCoords.y - (position > 0 ? textHeight + 2 * padding : -2 * padding)
            if (['supermillennium', 'grandSupercycle', 'primary', 'minute', 'micro'].indexOf(degree) !== -1) {
                const centerX = startX + textWidth / 2
                const centerY = startY + textHeight / 2 + 1.5 * window.devicePixelRatio

                const radius = Math.max(textWidth, textHeight) / 2 + 5 * window.devicePixelRatio

                ctx.beginPath()
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
                ctx.stroke()
                ctx.closePath()
            }
            ctx.fillStyle = this._settings.lineColor.color
            ctx.beginPath()
            ctx.closePath()
            ctx.fillStyle = this._settings.lineColor.color
            ctx.fillText(text, startX, startY)
        })
    }

    getFontSize(): number {
        const { degree } = this._settings

        if (['supermillennium', 'millennium', 'submillennium'].indexOf(degree) !== -1) {
            return 20
        }
        if (['grandSupercycle', 'supercycle', 'cycle'].indexOf(degree) !== -1) {
            return 18
        }
        if (['primary', 'intermediate', 'minor'].indexOf(degree) !== -1) {
            return 16
        }
        if (['minute', 'minuette', 'subminuette'].indexOf(degree) !== -1) {
            return 13
        }
        if (['micro', 'submicro', 'minuscule'].indexOf(degree) !== -1) {
            return 10
        }

        return 12
    }

    getText(str: string): string {
        const { degree } = this._settings
        const { triangleWave } = this.options
        let text = str.toString().toUpperCase()
        if (['grandSupercycle', 'supercycle', 'cycle'].indexOf(degree) !== -1) {
            text = triangleWave ? str.toLowerCase() : romanNumbers[Number(str)]
        }

        if (['minute', 'minuette', 'subminuette'].indexOf(degree) !== -1) {
            text = triangleWave ? str.toLowerCase() : lowerRomanNumbers[Number(str)]
        }

        if (['millennium', 'supercycle', 'intermediate', 'minuette', 'submicro'].indexOf(degree) !== -1) {
            text = `(${text})`
        }
        return text
    }

    calculateLineAttributes() {
        const { a, b, c, d, e, f } = this._state

        let prevPoint: Point;
        [a, b, c, d, e, f].forEach((p) => {
            if (!p) return
            let x = this.timeToCoordinate(p.x)
            let y = this.priceToCoordinate(p.y)
            let currentCoordinates: PointCoordinates = { x: 0, y: 0 }
            if (x && y) {
                currentCoordinates = {
                    x: scaledCoordinate(x, this.horizontalPixelRatio),
                    y: scaledCoordinate(y, this.verticalPixelRatio)
                }

                this.scaledCoordinates[p.id] = currentCoordinates

                if (prevPoint) {
                    const key = `${prevPoint.id}${p.id}`
                    this.lengths[key] = Math.abs(p.y - prevPoint.y)
                    const prevCoords = this.scaledCoordinates[prevPoint.id]
                    const slope = (currentCoordinates.y - prevCoords.y) / (currentCoordinates.x - prevCoords.x)
                    const intercept = currentCoordinates.y - slope * currentCoordinates.x
                    this.slopes[key] = slope
                    this.intercepts[key] = intercept
                }
            }
            prevPoint = p
        })
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = false) {
        if (setLineType) {
            const dotWidth = Math.max(2, this._settings.lineWidth) * this.horizontalPixelRatio
            let lineDash: number[] = [dotWidth, dotWidth]
            ctx.setLineDash(lineDash)
        }
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
        ctx.strokeStyle = this._settings.lineColor.color
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { a, b, c, d, e, f } = this.scaledCoordinates
        if (!a || !b || !c || !d || !e || !f) return null

        const d1 = getDistanceFromLine(scaledX, scaledY, this.slopes.ab, this.intercepts.ab)
        const d2 = getDistanceFromLine(scaledX, scaledY, this.slopes.bc, this.intercepts.bc)
        const d3 = getDistanceFromLine(scaledX, scaledY, this.slopes.cd, this.intercepts.cd)
        const d4 = getDistanceFromLine(scaledX, scaledY, this.slopes.de, this.intercepts.de)
        const d5 = getDistanceFromLine(scaledX, scaledY, this.slopes.ef, this.intercepts.ef)
        const d6 = getDistanceFromLine(scaledX, scaledY, this.slopes.fg, this.intercepts.fg)
        if (d1 <= THRESHOLD || d2 <= THRESHOLD || d3 <= THRESHOLD || d4 <= THRESHOLD || d5 <= THRESHOLD || d6 <= THRESHOLD) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'a',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.a = point
        } else if (this.lastAddedPoint.id == 'a') {
            point.id = 'b'
            this._state.b = point
        } else if (this.lastAddedPoint.id == 'b') {
            point.id = 'c'
            this._state.c = point
        } else if (this.lastAddedPoint.id == 'c') {
            point.id = 'd'
            this._state.d = point
        } else if (this.lastAddedPoint.id == 'd') {
            point.id = 'e'
            this._state.e = point
        } else {
            point.id = 'f'
            this._state.f = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const { a, b, c, d, e, f } = this._state

        return [a!, b!, c!, d!, e!, f!].filter(p => p)
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'a':
                    this._state.a = h
                    break
                case 'b':
                    this._state.b = h
                    break
                case 'c':
                    this._state.c = h
                    break
                case 'd':
                    this._state.d = h
                    break
                case 'e':
                    this._state.e = h
                    break
                case 'f':
                    this._state.f = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        if (!this._state[id as keyof typeof this._state]) return
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { a, b, c, d, e, f } = this._state
        const points = [a, b, c, d, e, f].filter(v => v)
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => a!.y - b!.y)
        const configs: LabelAxisViewConfig<number>[] = [
            {
                id: 'outer',
                p1: sorted[0]!.y,
                p2: sorted[sorted.length - 1]!.y,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'price-' + sorted[c]!.id,
                p1: sorted[c]!.y,
                p2: sorted[c]!.y,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }
        return configs
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { a, b, c, d, e, f } = this._state
        const points = [a, b, c, d, e, f].filter(v => v)
        if (points.length < 2) return []
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => (a!.x as number) - (b!.x as number))
        const configs: LabelAxisViewConfig<Time>[] = [
            {
                id: 'time',
                p1: sorted[0]!.x,
                p2: sorted[sorted.length - 1]!.x,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'time-' + sorted[c]!.id,
                p1: sorted[c]!.x,
                p2: sorted[c]!.x,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }

        return configs
    }

    getAvailableSettings = (): DrawingSettingGroup<ElliottImpulseWaveSettings, SettingValues>[] => {
        const { triangleWave } = this.options
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: triangleWave ? '#ff9800' : '#2c7bf4',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'degree',
                    label: 'Degree',
                    type: SettingType.SELECT,
                    defaultValue: 'intermediate',
                    options: waveDegreeOptions,
                    custom: true,
                    visible: true
                },

                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'a',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point2',
                    label: '#2',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'b',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point3',
                    label: '#3',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'c',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point4',
                    label: '#4',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'd',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point5',
                    label: '#5',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'e',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point6',
                    label: '#6',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'f',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    onSettingsUpdate = (settings: Partial<ElliottImpulseWaveSettings>) => {
        if (settings.point1) {
            this._state.a = settings.point1
        }
        if (settings.point2) {
            this._state.b = settings.point2
        }
        if (settings.point3) {
            this._state.c = settings.point3
        }
        if (settings.point4) {
            this._state.d = settings.point4
        }
        if (settings.point5) {
            this._state.e = settings.point5
        }
        if (settings.point6) {
            this._state.f = settings.point6
        }
    }

    getSettingsValues(): ElliottImpulseWaveSettings {
        const settings = { ...this._settings }
        if (this._state.a) {
            settings.point1 = this._state.a
        }
        if (this._state.b) {
            settings.point2 = this._state.b
        }
        if (this._state.c) {
            settings.point3 = this._state.c
        }
        if (this._state.d) {
            settings.point4 = this._state.d
        }
        if (this._state.e) {
            settings.point5 = this._state.e
        }
        if (this._state.f) {
            settings.point6 = this._state.f
        }
        return settings
    }

    getDrawingType(): DrawingTypes {
        return 'elliottImpulseWave'
    }

    getMiniSettings = () => {
        return ['fillColor', 'lineColor', 'lineWidth'] as (keyof ElliottImpulseWaveSettings)[]
    }
}

export default ElliottImpulseWaveTool